import React, { useState, useMemo } from 'react';
import cx from 'classnames';
import { useStaticQuery, graphql as gatsbyGraphql, Link, GatsbyLinkProps } from 'gatsby';
import { NavigationQuery } from '../../graphql-gatsby-types';
import { PageNavigation, PageNavigationItem } from './PageNavigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isIndexPage, slugToPath, getCategoryHref } from '../utils';
import { ProductSelect } from './ProductSelect';
import { BreadcrumbSeparator } from './BreadcrumbSeparator';
import xMarkIcon from '../images/xmark-icon.svg';
import { SidebarLink } from './SidebarLink';
import { getBreadcrumbTrail } from '../utils/get-breadcrumb';

const SIGNATURES_CATEGORIES = ['Getting Started', 'GraphQL', 'Integrations', 'Guides', 'Webhooks'];

const VERIFY_CATEGORIES = [
  'Getting Started',
  'How it works',
  'eIDs',
  'Guides & Tools',
  'Extensions',
  'Integrations',
  'Reference',
];

interface Props {
  path: string | undefined;
  hidden?: boolean;
  onLinkClick?: () => void;
}

const navigationQuery = gatsbyGraphql`query Navigation {
  signaturesPages: allMdx(
    filter: {frontmatter: {product: {eq: "signatures"}}}
    sort: {frontmatter: {sort: ASC}}
  ) {
    edges {
      node {
        __typename
        id
        fields {
          slug
        }
        internal {
          contentFilePath
        }
        frontmatter {
          title
          category
          subcategory
          isSubIndex
        }
      }
    }
  }
  verifyPages: allMdx(
    filter: {frontmatter: {product: {eq: "verify"}}}
    sort: {frontmatter: {sort: ASC}}
  ) {
    edges {
      node {
        __typename
        id
        fields {
          slug
        }
        internal {
          contentFilePath
        }
        frontmatter {
          title
          category
          subcategory
          isSubIndex
        }
      }
    }
  }
}`;

export function useNavigationData() {
  const data = useStaticQuery<NavigationQuery>(navigationQuery);

  return useMemo(
    () => ({
      signaturesPages: data.signaturesPages.edges.map(edge => edge.node),
      verifyPages: data.verifyPages.edges.map(edge => edge.node),
    }),
    [data],
  );
}

export default function Navigation(props: Props) {
  const { path, onLinkClick } = props;
  const isVerify = path?.startsWith('/verify');
  const isSignatures = path?.startsWith('/signatures');

  const { signaturesPages, verifyPages } = useNavigationData();

  /* Homepage navigation */
  if (!isVerify && !isSignatures) {
    return (
      <>
        <ProductSelect />
        <ul>
          {/* Verify */}
          <li>
            <Link
              to="/verify"
              className="block mb-3 font-medium text-primary-600 text-lg hover:text-light-blue-900"
              onClick={onLinkClick}
            >
              Idura Verify
            </Link>
            <ul className="space-y-2 border-l border-white text-md font-normal">
              {VERIFY_CATEGORIES.map(category => {
                const href = getCategoryHref(category, verifyPages);
                if (!href) return null;

                return (
                  <li key={category}>
                    <SidebarLink to={href} onClick={onLinkClick}>
                      {category}
                    </SidebarLink>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Signatures */}
          <li className="mt-8">
            <Link
              to="/signatures"
              className="block mb-3 font-medium text-primary-600 text-lg hover:text-light-blue-900"
              onClick={onLinkClick}
            >
              Idura Signatures
            </Link>
            <ul className="space-y-2 border-l border-white text-md font-normal">
              {SIGNATURES_CATEGORIES.map(category => {
                const href = getCategoryHref(category, signaturesPages);
                if (!href) return null;

                return (
                  <li key={category}>
                    <SidebarLink to={href} onClick={onLinkClick}>
                      {category}
                    </SidebarLink>
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </>
    );
  }

  /* Product-specific navigation */
  const categories = isSignatures ? SIGNATURES_CATEGORIES : VERIFY_CATEGORIES;
  const pages = isSignatures ? signaturesPages : verifyPages;

  return (
    <>
      <ProductSelect />
      <ul>
        {categories.map((category, index) => {
          const categoryHref = getCategoryHref(category, pages);
          return (
            <li key={category} className={index > 0 ? 'mt-8' : ''}>
              {/* Category header */}
              {categoryHref ? (
                <Link
                  to={categoryHref}
                  getProps={({ isCurrent }) => ({
                    className: cx(
                      'block mb-3 font-medium',
                      isCurrent
                        ? 'text-light-blue-900'
                        : 'text-primary-600 hover:text-light-blue-900',
                    ),
                  })}
                  onClick={onLinkClick}
                >
                  {category}
                </Link>
              ) : (
                <h5 className="mb-3 font-medium text-primary-600">{category}</h5>
              )}

              {/* Pages */}
              <ul className="space-y-2 border-l border-white text-md font-normal">
                {pages
                  .filter(
                    node =>
                      (!isIndexPage(node) || node.frontmatter?.isSubIndex) &&
                      node.frontmatter?.category === category,
                  )
                  .map(page => (
                    <li key={page.id}>
                      <SidebarLink to={slugToPath(page.fields!.slug!)} onClick={onLinkClick}>
                        {page.frontmatter!.title}
                      </SidebarLink>
                    </li>
                  ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function DesktopNavigation(props: Props) {
  return (
    <div
      className={cx(
        'hidden border-r border-light-blue-300/40 fixed z-20 inset-0 top-[51px] left-0 pl-[calc(50%-768px)] right-auto overflow-y-auto',
        { 'lg:block': !props.hidden },
      )}
    >
      <div className="w-[17.5rem] py-10 pl-8">
        <Navigation {...props} />
      </div>
    </div>
  );
}

type MobileProps = Props & {
  frontmatter: any;
  pageNavigationItems?: PageNavigationItem[];
};
export function MobileNavigation(props: MobileProps) {
  const { path, frontmatter } = props;
  const [showNavigation, setShowNavigation] = useState(false);
  const [showPageNavigation, setPageShowNavigation] = useState(false);

  const { signaturesPages, verifyPages } = useNavigationData();

  const cleanPath = (path || '').split('?')[0].replace(/\/+$/, '');
  const isSignatures = path?.startsWith('/signatures');

  const pages = isSignatures ? signaturesPages : verifyPages;

  const categoryHref = getCategoryHref(frontmatter?.category, pages);

  const getMobileBreadcrumb = () => {
    if (!cleanPath) return { category: 'Welcome' };

    let product = undefined;
    if (cleanPath.startsWith('/verify')) product = 'verify';
    if (cleanPath.startsWith('/signatures')) product = 'signatures';

    // If exact match on product root, return only product
    if (cleanPath === `/${product}`) return { product };

    // Otherwise return full details
    const breadcrumb = getBreadcrumbTrail(path || '', frontmatter, { signaturesPages, verifyPages });
    return {
      breadcrumb,
      product,
      category: frontmatter?.category,
      subcategory: frontmatter?.subcategory,
      title: frontmatter?.title,
    };
  };

  const { product, category, subcategory, title, breadcrumb } = getMobileBreadcrumb();

  const currentNode = pages.find(n => slugToPath(n.fields!.slug!) === `${cleanPath}/`);
  const isCategoryIndexPage = currentNode
    ? isIndexPage(currentNode) && !currentNode.frontmatter?.isSubIndex
    : false;

  // Show the title in the breadcrumb on mobile if it's not a category index page.
  const showTitleInBreadcrumb = title && !isCategoryIndexPage;

  // When breadcrumb trail contains more than 2 segments, make it horizontally scrollable on mobile, e.g.
  // /verify/reference/errors/danish-mitid/MITID_NONCE_COOKIE_MISSING/ 
  // /verify/reference/authorization-flows/authorization-code-flow/
  const segmentsCount =
    (product ? 1 : 0) +
    (breadcrumb?.length ?? 0) +
    (category ? 1 : 0) +
    (showTitleInBreadcrumb ? 1 : 0);

  const shouldScroll = segmentsCount > 2;

  return (
    <React.Fragment>
      <div
        className="flex justify-between lg:hidden sticky z-30 backdrop-blur duration-500 border-light-blue-300/40 border-b supports-backdrop-blur:bg-white/60 p-4 sm:px-6 md:px-8 top-[45px]"
      >
        <div className="flex min-w-0">
          <button
            type="button"
            className="text-light-blue-600 mr-4"
            onClick={() => setShowNavigation(true)}
          >
            <span className="sr-only">Navigation</span>
            <svg width="24" height="24">
              <path
                d="M5 6h14M5 12h14M5 18h14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              ></path>
            </svg>
          </button>
          <ol
            className={cx(
              'flex flex-1 items-center gap-0 text-sm leading-6 min-w-0',
              shouldScroll
                ? 'flex-nowrap whitespace-nowrap overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] pr-4'
                : 'flex-wrap whitespace-normal overflow-visible',
            )}
          >
            {product && (
              <li className="flex items-center shrink-0">
                <Link to={`/${product}`}>{product === 'verify' ? 'Verify' : 'Signatures'}</Link>
                {(category || breadcrumb) && <BreadcrumbSeparator />}
              </li>
            )}

            {breadcrumb &&
              breadcrumb.map((i, index) => (
                <li key={i.href || index} className="shrink-0 flex items-center">
                  <a href={i.href}>{i.label}</a>
                  <BreadcrumbSeparator />
                </li>
              ))}

            {category ? (
              <li className="shrink-0 flex items-center">
                <span className="shrink-0 whitespace-nowrap">
                  {categoryHref ? (
                    <Link to={categoryHref}>{category}</Link>
                  ) : (
                    <span>{category}</span>
                  )}
                </span>
                {(subcategory || showTitleInBreadcrumb) && <BreadcrumbSeparator />}
              </li>
            ) : null}

            {subcategory ? (
              <li className="shrink-0 flex items-center">
                <span className="shrink-0 whitespace-nowrap">{subcategory}</span>
                {showTitleInBreadcrumb && <BreadcrumbSeparator />}
              </li>
            ) : null}

            {showTitleInBreadcrumb ? (
              <li
                className={cx(
                  'flex items-center min-w-0',
                  shouldScroll ? 'shrink-0 flex-none' : 'flex-1',
                )}
              >
                <span
                  className={cx(
                    'font-medium text-light-blue-900',
                    shouldScroll
                      ? 'shrink-0 whitespace-nowrap'
                      : 'flex-1 min-w-0 whitespace-normal break-words',
                  )}
                >
                  {title}
                </span>
              </li>
            ) : null}
          </ol>
        </div>

        {props.pageNavigationItems ? (
          <button
            type="button"
            className="text-light-blue-600"
            onClick={() => setPageShowNavigation(true)}
          >
            <span className="sr-only">Table of contents</span>
            <FontAwesomeIcon icon={`fa-solid fa-book-open` as any} />
          </button>
        ) : null}
      </div>

      <div
        className={`fixed z-50 inset-0 overscroll-contain overflow-y-auto ${showNavigation ? 'block' : 'hidden'}`}
      >
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setShowNavigation(false)}
        ></div>
        <div
          className="relative bg-white min-h-full w-80 max-w-[calc(100%-3rem)] p-6"
          onClick={event => event.stopPropagation()}
        >
          <button
            onClick={() => setShowNavigation(false)}
            type="button"
            className="absolute z-10 top-5 right-5 w-8 h-8 flex items-center justify-center"
          >
            <span className="sr-only">Close navigation</span>
            <img src={xMarkIcon} alt="Close navigation" className="cursor-pointer" />
          </button>
          <Navigation {...props} onLinkClick={() => setShowNavigation(false)} />
        </div>
      </div>

      {props.pageNavigationItems ? (
        <div
          className={`fixed z-50 inset-0 overscroll-contain overflow-y-auto ${showPageNavigation ? 'block' : 'hidden'}`}
        >
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setPageShowNavigation(false)}
          ></div>
          <div
            className="absolute bg-light-blue-25 min-h-full w-80 max-w-[calc(100%-3rem)] p-6 right-0"
            onClick={event => event.stopPropagation()}
          >
            <button
              onClick={() => setPageShowNavigation(false)}
              type="button"
              className="absolute z-10 top-5 right-5 w-8 h-8 flex items-center justify-center"
            >
              <span className="sr-only">Close table of contents</span>
              <img src={xMarkIcon} alt="Close table of contents" className="cursor-pointer" />
            </button>

            <PageNavigation
              items={props.pageNavigationItems}
              onNavigate={() => setPageShowNavigation(false)}
            />
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}
